import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { portalApi, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, ArrowRight, Images, CreditCard, CalendarCheck, Tablet, Eye, EyeOff } from "lucide-react";
import { LogoBspark } from "@/components/LogoBspark";
import { SelectorIdioma } from "@/components/SelectorIdioma";
import { PieLegal } from "@/components/PieLegal";
import { useIdioma } from "@/context/IdiomaContext";
import { toast } from "sonner";

const VENTAJAS = [
  { icon: CalendarCheck, clave: "v1" },
  { icon: Images, clave: "v2" },
  { icon: CreditCard, clave: "v3" },
];

export default function PortalHome() {
  const { data, login } = usePortalAuth();
  const { t } = useIdioma();
  const navigate = useNavigate();
  const [matricula, setMatricula] = useState("");
  const [password, setPassword] = useState("");
  const [verPass, setVerPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [olvido, setOlvido] = useState(false);
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    if (data) navigate("/portal", { replace: true });
  }, [data, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(matricula, password);
    setLoading(false);
    if (res.ok) navigate("/portal");
    else setError(res.error);
  };

  const recuperar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const { data } = await portalApi.post("/portal/password/olvidada", { email });
      toast.success(data.mensaje);
      setEnviado(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <LogoBspark className="h-16 w-16" texto={false} />
          <div>
            <p className="font-display font-extrabold text-lg leading-none">FamilyHub</p>
            <p className="text-xs text-muted-foreground mt-1">{t("portal.areaFamilias")}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <SelectorIdioma />
          <Link to="/admin" data-testid="admin-access-link"
            className="text-xs text-muted-foreground/70 hover:text-foreground transition-colors duration-200">
            {t("portal.personal")}
          </Link>
          <Link to="/recepcion" data-testid="recepcion-access-link"
            className="text-xs font-semibold text-primary hover:opacity-80 transition-opacity duration-200 flex items-center gap-1.5">
            <Tablet className="h-3.5 w-3.5" /> {t("portal.recepcion")}
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pb-20 pt-6 grid lg:grid-cols-2 gap-14 items-start">
        <div className="fade-up">
          <p className="uppercase tracking-[0.3em] text-xs text-primary font-semibold mb-5">{t("home.kicker")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03]">
            {t("home.titulo1")} <br />{t("home.titulo2")}
          </h1>
          <p className="text-muted-foreground mt-6 max-w-md text-base">
            {t("home.sub")}
          </p>

          <div className="mt-12 space-y-5">
            {VENTAJAS.map(({ icon: Icon, clave }) => (
              <div key={clave} className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center shrink-0">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{t(`home.${clave}t`)}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{t(`home.${clave}d`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Card className="rounded-2xl p-7 sm:p-9 border-border shadow-lg fade-up">
          {!olvido ? (
            <>
              <h2 className="font-display text-2xl font-extrabold">{t("portal.login")}</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-7">
                {t("login.ayuda")}
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="matricula">{t("portal.matricula")}</Label>
                  <Input id="matricula" required value={matricula} placeholder="BSP-00001"
                    onChange={(e) => setMatricula(e.target.value)} className="rounded-lg h-12"
                    data-testid="portal-matricula-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("portal.password")}</Label>
                  <div className="relative">
                    <Input id="password" type={verPass ? "text" : "password"} required value={password}
                      onChange={(e) => setPassword(e.target.value)} className="rounded-lg h-12 pr-12"
                      data-testid="portal-password-input" />
                    <button type="button" onClick={() => setVerPass((v) => !v)}
                      data-testid="ver-password-btn" aria-label={t("portal.verPassword")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200">
                      {verPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <p className="text-sm text-destructive font-medium" data-testid="portal-login-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="portal-login-submit"
                  className="w-full h-12 rounded-full font-semibold text-base transition-transform duration-200 hover:-translate-y-[1px]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("portal.login")}
                </Button>
              </form>
              <button onClick={() => setOlvido(true)} data-testid="olvide-password-btn"
                className="text-sm text-muted-foreground hover:text-foreground mt-5 transition-colors duration-200">
                {t("portal.olvido")}
              </button>

              <div className="mt-8 pt-7 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4">{t("portal.sinCuenta")}</p>
                <Button asChild variant="outline" className="w-full h-12 rounded-full font-semibold" data-testid="ir-registro-btn">
                <Link to="/registro">
                  {t("login.registrar")} <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-extrabold">{t("recuperar.titulo")}</h2>
              {enviado ? (
                <div data-testid="recuperar-enviado">
                  <p className="text-sm text-muted-foreground mt-3">
                    {t("recuperar.enviado")}
                  </p>
                  <Button className="w-full h-12 rounded-full font-semibold mt-7"
                    onClick={() => { setOlvido(false); setEnviado(false); setEmail(""); }}
                    data-testid="volver-login-btn">
                    {t("recuperar.volver")}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mt-2 mb-7">
                    {t("recuperar.ayuda")}
                  </p>
                  <form onSubmit={recuperar} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="email-rec">{t("comun.email")}</Label>
                      <Input id="email-rec" type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)} className="rounded-lg h-12"
                        data-testid="recuperar-email-input" />
                    </div>
                    <Button type="submit" disabled={enviando} data-testid="recuperar-submit-btn"
                      className="w-full h-12 rounded-full font-semibold">
                      {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : t("recuperar.enviar")}
                    </Button>
                  </form>
                  <button onClick={() => setOlvido(false)}
                    className="text-sm text-muted-foreground hover:text-foreground mt-5 transition-colors duration-200">
                    {t("recuperar.volver")}
                  </button>
                </>
              )}
            </>
          )}
        </Card>
      </main>
      <PieLegal />
    </div>
  );
}
